import React from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Heart, Users, Music, Camera, Video } from 'lucide-react';
import { currentPagePath } from '../utils/redirect';

const SignupInviteModal = ({ isOpen, onClose, contentType = 'content' }) => {
    const navigate = useNavigate();

    if (!isOpen) return null;

    const getContentIcon = () => {
        switch (contentType) {
            case 'audio': return <Music className="w-8 h-8" />;
            case 'video': return <Video className="w-8 h-8" />;
            case 'image': return <Camera className="w-8 h-8" />;
            case 'post': return <Users className="w-8 h-8" />;
            default: return <Heart className="w-8 h-8" />;
        }
    };

    const getContentMessage = () => {
        switch (contentType) {
            case 'audio': return 'Love this track? Show your support!';
            case 'video': return 'Love this video? Show your support!';
            case 'image': return 'Love this image? Show your support!';
            case 'post': return 'Love this post? Show your support!';
            default: return 'Love this content? Show your support!';
        }
    };

    const handleLogin = () => {
        onClose();
        navigate(`/login?redirect=${encodeURIComponent(currentPagePath())}`);
    };

    const handleSignup = () => {
        onClose();
        navigate(`/register?redirect=${encodeURIComponent(currentPagePath())}`);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const modalContent = (
        <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={handleBackdropClick}
        >
            {/* This modal renders via a portal, so DOM-wise it's detached from
                wherever it was triggered — but React still bubbles clicks through
                the component tree it was rendered from, not the DOM tree. Without
                stopping that here, clicking anything inside (especially the close
                button) can also fire whatever ancestor onClick/Link the triggering
                card happens to have (varies per page), which is what made the X
                button seem to work in some sections but not others. */}
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-gradient-to-br from-gray-900 via-purple-900/30 to-gray-900 rounded-2xl border border-purple-500/30 p-8 max-w-md w-full relative shadow-2xl">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700/50 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Content */}
                <div className="text-center">
                    {/* Animated Hearts */}
                    <div className="flex justify-center items-center mb-6 relative">
                        <div className="absolute animate-pulse">
                            <Heart className="w-16 h-16 text-red-400 fill-current opacity-20" />
                        </div>
                        <div className="text-purple-400 animate-bounce">
                            {getContentIcon()}
                        </div>
                    </div>

                    {/* Main Message */}
                    <h2 className="text-2xl font-bold text-white mb-2">
                        {getContentMessage()}
                    </h2>
                    
                    <p className="text-gray-300 mb-6 leading-relaxed">
                        Join The Deep Wave community to like, comment, and connect with creators. 
                        <span className="text-purple-400 font-semibold"> It's free and takes just a minute!</span>
                    </p>

                    {/* Benefits */}
                    <div className="bg-gray-800/50 rounded-xl p-4 mb-6 text-left">
                        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4 text-blue-400" />
                            Join to unlock:
                        </h3>
                        <ul className="space-y-2 text-sm text-gray-300">
                            <li className="flex items-center gap-2">
                                <Heart className="w-4 h-4 text-red-400 fill-current" />
                                Like and support your favorite content
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-4 h-4 text-center text-blue-400">💬</span>
                                Comment and engage with creators
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-4 h-4 text-center text-green-400">🎵</span>
                                Upload and share your own content
                            </li>
                            <li className="flex items-center gap-2">
                                <span className="w-4 h-4 text-center text-purple-400">🌊</span>
                                Discover new artists and trends
                            </li>
                        </ul>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={handleSignup}
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg"
                        >
                            Create Account
                        </button>
                        <button
                            onClick={handleLogin}
                            className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-colors border border-gray-600 hover:border-gray-500"
                        >
                            Login
                        </button>
                    </div>

                    {/* Footer */}
                    <p className="text-xs text-gray-500 mt-4">
                        Already have an account? Just click Login above!
                    </p>
                </div>
            </div>
        </div>
    );

    return isOpen ? ReactDOM.createPortal(modalContent, document.body) : null;
};

export default SignupInviteModal;